/**
 * Imperial CMS — polskie tłumaczenia visitor-facing messages.
 *
 * Wszystkie stringi tu są ZAWSZE widoczne dla użytkownika strony publicznej —
 * nie zawierają technicznych szczegółów (IADMIN paths, internal naming itp.).
 *
 * **Konwencja:** klucze top-level grupują obszary funkcjonalne (`maintenance`,
 * `notFound`, `homepage`). Wartości to plain strings (bez ICU pluralization
 * dla MVP). Komponenty React-CMS import'ują przez `pickTranslations`.
 *
 * @see ../index.ts — pickTranslations helper
 * @see ./en.ts, ./de.ts — pozostałe locales
 */

/**
 * Shape contract — wymuszany na wszystkich locales. Definicja structural
 * (strings, nie literals) żeby `en.ts`/`de.ts` mogły mieć własne wartości.
 *
 * **Konwencja:** dodanie nowego klucza w PL -> TypeScript wymusza dodanie w
 * pozostałych locales (`Record<Locale, MessagesShape>` w `lib/i18n/index.ts`).
 */
export type MessagesShape = {
  maintenance: {
    title: string;
    subtitle: string;
    body: string;
    footnote: string;
  };
  /** "Serwis aktualnie niedostępny" — pokazywany visitor'owi gdy admin włączył
   *  `cmsConfig.service_disabled` (tymczasowe wyłączenie strony). Admin/zalogowany
   *  użytkownik nadal widzi normalny render. */
  serviceDisabled: {
    title: string;
    subtitle: string;
    body: string;
    footnote: string;
  };
  notFound: {
    /** Krótki tytuł — używany w `<title>` (np. "Nie znaleziono"). */
    title: string;
    /** Pełny opis — body 404 page. */
    body: string;
    /** Etykieta link'a do strony głównej. */
    homeLink: string;
  };
  /** "Treść dostępna po zalogowaniu" — pokazywany w obszarze treści katalogu
   *  gdy podstrona ma `after_login=1` (imp_cms_catalog), a visitor nie jest
   *  zalogowany. Chrome (nagłówek z przyciskiem logowania, stopka) pozostaje
   *  widoczny — komunikat zastępuje tylko treść katalogu. */
  loginRequired: {
    title: string;
    body: string;
    /** Etykieta przycisku przekierowującego do strony logowania (c_user_accounts). */
    loginButton: string;
    /** Etykieta link'a powrotu do strony głównej. */
    homeLink: string;
  };
  /** Pełnoekranowy lockout logowania - flaga `cms_login_required` (cała domena
   *  wymaga logowania). Zastępuje całą stronę formularzem logowania. */
  globalLoginGate: {
    title: string;
    message: string;
    loginLabel: string;
    passwordLabel: string;
    submit: string;
    submitting: string;
    failed: string;
  };
  homepage: {
    backendErrorTitle: string;
    redirectLoopTitle: string;
    redirectLoopMessage: string;
    redirectLoopHint: string;
  };
  /** Wspólne labelki używane w wielu komponentach (dev fallbacki, paginacja). */
  common: {
    /** CTA do dokumentacji (FallbackMessage w CatalogRenderer/WindowRenderer). */
    readDocs: string;
    /** Pagination "next page" link. */
    paginationNext: string;
  };
};

export const messagesPl: MessagesShape = {
  /* Maintenance page — visitor widzi gdy strona nie może być wyrenderowana
     z powodu konfiguracyjnych issue (np. invalid_for_template). */
  maintenance: {
    title: "Chwila moment",
    subtitle: "Pracujemy nad tą sekcją",
    body: "Wrócimy z nią za chwilę. Strona odświeży się automatycznie.",
    footnote: "Dziękujemy za cierpliwość.",
  },

  /* Serwis tymczasowo wyłączony — admin świadomie wyłączył dostęp przez
     `cmsConfig.service_disabled`. Komunikat skierowany do visitora. */
  serviceDisabled: {
    title: "Strona w trakcie aktualizacji",
    subtitle: "Serwis aktualnie niedostępny",
    body: "Przepraszamy za niedogodności. Zapraszamy ponownie za chwilę — strona odświeży się automatycznie.",
    footnote: "Dziękujemy za cierpliwość.",
  },

  /* 404 / not found — używane na 404 page + jako title dla niepasujących URL'i. */
  notFound: {
    title: "Nie znaleziono",
    body: "Adres, którego szukasz, nie istnieje lub został przeniesiony.",
    homeLink: "Wróć do strony głównej",
  },

  /* Treść dostępna tylko dla zalogowanych — podstrona z after_login=1. */
  loginRequired: {
    title: "Treść dostępna po zalogowaniu",
    body: "Aby zobaczyć tę stronę, musisz być zalogowany. Po zalogowaniu wrócisz dokładnie w to miejsce.",
    loginButton: "Zaloguj się",
    homeLink: "Wróć do strony głównej",
  },

  /* Pełnoekranowy lockout - cała domena wymaga logowania (cms_login_required). */
  globalLoginGate: {
    title: "Wymagane logowanie",
    message: "Aby uzyskać dostęp do treści tej strony, musisz się zalogować.",
    loginLabel: "Login lub e-mail",
    passwordLabel: "Hasło",
    submit: "Zaloguj się",
    submitting: "Logowanie...",
    failed: "Nieprawidłowy login lub hasło.",
  },

  /* Errory na homepage (komunikacja z backendem / konfiguracja). */
  homepage: {
    backendErrorTitle: "Błąd komunikacji z backendem",
    redirectLoopTitle: "Błąd konfiguracji strony głównej",
    redirectLoopMessage:
      "Strona główna jest aktualnie niedostępna z powodu błędnej konfiguracji " +
      "pola przekierowania — wskazany adres wskazuje z powrotem na stronę " +
      "główną, co tworzy pętlę przekierowań.",
    redirectLoopHint: "Prosimy skontaktować się z administratorem serwisu.",
  },

  /* Wspólne labelki — dev fallbacki + paginacja. */
  common: {
    readDocs: "Czytaj dokumentację",
    paginationNext: "Następna",
  },
};
